/**
 * System Alerts Service
 *
 * Checks for active system alerts/downtime notifications.
 * Uses the public v3 alerts endpoint.
 */

import { getEndpoint } from '../config/endpoints.js';
import { resolveTenantId } from './api.service.js';
import { authService } from './auth.service.js';
import { logger } from '../utils/logger.js';

const ALERTS_TIMEOUT_MS = 5000;

class AlertsService {
    /**
     * Check for active system alerts
     * @returns {Promise<{hasAlerts: boolean, alerts: Array}>}
     */
    async checkAlerts() {
        try {
            const apiUrl = getEndpoint('systemAlerts');

            // Server-authoritative tenant header for all requests
            const tenantId = resolveTenantId();

            // Proxy injects Authorization server-side
            const buildFetchOptions = () => ({
                method: 'GET',
                credentials: 'include',
                signal: AbortSignal.timeout(ALERTS_TIMEOUT_MS),
                headers: {
                    'Content-Type': 'application/json',
                    ...(tenantId && { tenant: tenantId }),
                    ...authService.getSessionHeaders()
                }
            });

            let response = await fetch(apiUrl, buildFetchOptions());

            if (response.status === 401) {
                logger.warn('[AlertsService] Session expired — re-initializing before retry');
                await authService.reinitialize();
                response = await fetch(apiUrl, buildFetchOptions());
            }

            if (!response.ok) {
                logger.warn(`[AlertsService] Alerts API returned ${response.status}`);
                return { hasAlerts: false, alerts: [] };
            }

            const data = await response.json();
            const alerts = data?.alerts || [];

            return {
                hasAlerts: alerts.length > 0,
                alerts
            };
        } catch (error) {
            logger.warn('[AlertsService] Failed to check alerts:', error.message);
            return { hasAlerts: false, alerts: [] };
        }
    }
}

export const alertsService = new AlertsService();
